import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, ExternalLink, Image as ImageIcon, Send } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { AssetDialog } from './AssetRecord';
import Button from '../ui/Button';
import Textarea from '../ui/Textarea';
import Tag from '../ui/Tag';
import Icon from '../ui/Icon';
import Skeleton from '../ui/Skeleton';
import { clean } from '../shell/copy';

// HD-7: the Clio chat body on src/ui primitives (DESIGN rev 2.1 §2). Behaviour is the pre-HD-7 component's:
// one POST /api/assistant/query per question with the last 6 turns as history (the API answers in one response, there
// is no token stream), the per-sessionKey history in sessionStorage with a 4-hour TTL, help mode from initialHelpMode
// (fresh thread, never restored), Enter sends, Shift+Enter is a newline, the field is disabled while a reply is pending.
// Two hosts: /assistant (mode="page", inside the HD-5 shell frame) and the Scripts editor drawer (mode="drawer").

const SESSION_TTL = 4 * 60 * 60 * 1000; // 4 hours

const WELCOME = {
  role:    'assistant',
  answer:  "I'm Clio, the production assistant. Ask about anything in the pipeline, for example which scripts are stuck in Qual, or which Dental Digest videos are in editing.",
  records: [],
};

const HELP_WELCOME = {
  role:    'assistant',
  answer:  'Ask how Heliconflow works: features, workflows, permissions, or what a button does. You can ask about pipeline data here too.',
  records: [],
};

// A thread saved before HD-7 opens with the old greeting; show the current one in its place.
const OLD_GREETING = "Hey \u2014 I'm Clio";

function loadSession(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const { messages, lastAt } = JSON.parse(raw);
    if (Date.now() - lastAt > SESSION_TTL) {
      sessionStorage.removeItem(key);
      return null;
    }
    if (messages?.[0]?.role === 'assistant' && messages[0].answer?.startsWith(OLD_GREETING)) return [WELCOME, ...messages.slice(1)];
    return messages;
  } catch {
    return null;
  }
}

function saveSession(key, messages) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ messages, lastAt: Date.now() }));
  } catch {}
}

const SUGGESTIONS = [
  'Which scripts have been in Qual for more than 5 days?',
  'Show me all approved videos with no posting slot yet',
  'Give me a pipeline health check. Does anything look off?',
];

const HELP_SUGGESTIONS = [
  'How do I send a script to production?',
  'What does each Live production status mean?',
  'How do I run a billing report for a project?',
  'What can a Producer do in Heliconflow?',
];

// A record the answer refers to: same routes as the retired RecordChip.
const RECORD_KIND = {
  script:          { label: 'Script', route: id => `/scripts?script=${id}` },
  live_production: { label: 'Live',   route: id => `/production/${id}` },
  ai_production:   { label: 'AI',     route: id => `/ai?card=${id}` },
};

function RecordChips({ records }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  if (!records?.length) return null;
  const visible = expanded ? records : records.slice(0, 6);
  const hidden  = records.length - 6;
  return (
    <div className="ui-cluster" data-testid="clio-records">
      {visible.map((r, i) => {
        const kind = RECORD_KIND[r.type];
        return (
          <span key={r.id + i} className="ui-chip ui-chip--static">
            <button type="button" className="ui-chip__main" title={clean(r.detail) || undefined}
              onClick={() => navigate(kind ? kind.route(r.id) : '/')}>
              {kind && <span className="ui-chip__tag">{kind.label}</span>}
              <span className="ui-chip__title">
                {clean(r.name)}
                {r.detail && <span className="ui-text-3">, {clean(r.detail)}</span>}
              </span>
            </button>
          </span>
        );
      })}
      {!expanded && hidden > 0 && (
        <Button variant="quiet" onClick={() => setExpanded(true)}>Show {hidden} more</Button>
      )}
    </div>
  );
}

function AssetTiles({ assets, onOpen }) {
  if (!assets?.length) return null;
  return (
    <div className="ui-tiles ui-tiles--2" data-testid="clio-assets">
      {assets.map(a => {
        const tags = a.aiTags || {};
        const topTags = [
          ...(tags.objects || []).slice(0, 1),
          ...(tags.brands  || []).slice(0, 1),
          ...(tags.themes  || []).slice(0, 1),
        ].filter(Boolean);
        return (
          <button key={a.id} type="button" className="ui-tile" onClick={() => onOpen(a.id)}>
            <span className="ui-tile__thumb">
              {a.thumbnailUrl
                ? <img src={a.thumbnailUrl} alt={a.fileName} className="ui-cover" />
                : <Icon icon={ImageIcon} label="No preview" />}
            </span>
            <span className="ui-tile__body">
              {a.aiDescription && <span className="ui-meta ui-clamp">{clean(a.aiDescription)}</span>}
              {topTags.length > 0 && (
                <span className="ui-cluster">
                  {topTags.map((t, i) => <Tag key={i}>{clean(t)}</Tag>)}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function AssistantMessage({ message, onAssetClick }) {
  if (message.loading) {
    return (
      <div className="ui-msg ui-msg--other" role="status" aria-label="Clio is answering">
        <span className="ui-stack ui-stack--tight">
          <Skeleton width={220} />
          <Skeleton width={140} />
        </span>
      </div>
    );
  }
  return (
    <div className="ui-msg ui-msg--other" data-testid="clio-msg" data-role="assistant">
      <div>{clean(message.answer)}</div>
      <RecordChips records={message.records} />
      <AssetTiles assets={message.assets} onOpen={onAssetClick} />
      {message.doc && (
        <a className="ui ui-btn ui-btn--secondary ui-fitself" href={message.doc.url} target="_blank" rel="noopener noreferrer">
          <Icon icon={BookOpen} />
          Read the {clean(message.doc.label)} docs
          <Icon icon={ExternalLink} label="Opens in a new tab" />
        </a>
      )}
    </div>
  );
}

// mode: 'page' | 'drawer'
// sessionKey: storage key so page and drawer histories are independent
// showHeader: page mode only. false when the host page puts the title in the shell's PageHeader (HD-5 /assistant).
export default function ClioChat({ mode = 'page', initialHelpMode = false, sessionKey = 'helicon_chat', showHeader = true }) {
  const { user } = useAuth();
  const isHelpMode = initialHelpMode;

  const restored = isHelpMode ? null : loadSession(sessionKey);
  const [messages,    setMessages]    = useState(restored || (isHelpMode ? [HELP_WELCOME] : [WELCOME]));
  const [input,       setInput]       = useState('');
  const [loading,     setLoading]     = useState(false);
  const [firstSent,   setFirstSent]   = useState(restored ? restored.length > 1 : false);
  const [openAssetId, setOpenAssetId] = useState(null);
  const threadRef = useRef(null);
  const inputRef  = useRef(null);

  const suggestions = isHelpMode ? HELP_SUGGESTIONS : SUGGESTIONS;

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
    saveSession(sessionKey, messages);
  }, [messages, sessionKey]);

  const submit = async (q) => {
    const question = q.trim();
    if (!question || loading) return;

    setFirstSent(true);
    setInput('');
    setMessages(prev => [
      ...prev,
      { role: 'user', question },
      { role: 'assistant', loading: true },
    ]);
    setLoading(true);

    const history = messages
      .filter(m => !m.loading && (m.role === 'user' ? !!m.question : !!m.answer))
      .slice(-6)
      .map(m => m.role === 'user'
        ? { role: 'user', content: m.question }
        : { role: 'assistant', content: m.queryDescription
            ? `[Query context: ${m.queryDescription}]\n${m.answer}`
            : m.answer }
      );

    try {
      const res = await api.post('/api/assistant/query', { question, history });
      setMessages(prev => {
        const next = [...prev];
        next[next.length - 1] = {
          role:             'assistant',
          answer:           res.data.answer,
          records:          res.data.records || [],
          assets:           res.data.assets  || [],
          doc:              res.data.doc     || null,
          queryDescription: res.data.queryDescription || '',
        };
        return next;
      });
    } catch {
      setMessages(prev => {
        const next = [...prev];
        next[next.length - 1] = {
          role:    'assistant',
          answer:  'Clio could not answer. Check your connection and send the question again.',
          records: [],
        };
        return next;
      });
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit(input);
    }
  };

  const assetModal = openAssetId && (
    <AssetDialog
      assetId={openAssetId}
      user={user}
      folders={[]}
      onClose={() => setOpenAssetId(null)}
    />
  );

  const thread = (
    <div className="ui-msgs">
      {messages.map((msg, i) =>
        msg.role === 'user' ? (
          <div key={i} className="ui-msg ui-msg--self" data-testid="clio-msg" data-role="user">{msg.question}</div>
        ) : (
          <AssistantMessage key={i} message={msg} onAssetClick={id => setOpenAssetId(id)} />
        )
      )}
    </div>
  );

  // Pinned under the thread: suggested questions until the first one is sent, then the field and Send.
  const composer = (
    <form className="ui ui-composer" aria-label="Ask Clio" data-testid="clio-composer"
      onSubmit={e => { e.preventDefault(); submit(input); }}>
      <div className={mode === 'drawer' ? 'ui-stack' : 'ui-stack ui-col'}>
        {!firstSent && (
          <div className="ui-stack ui-stack--tight" data-testid="clio-suggestions">
            {suggestions.map((s, i) => (
              <Button key={i} variant="quiet" className="ui-wraplabel" onClick={() => submit(s)}>{s}</Button>
            ))}
          </div>
        )}
        <Textarea
          ref={inputRef}
          aria-label="Question for Clio"
          placeholder={mode === 'drawer' ? 'Ask Clio anything' : 'Ask Clio anything about production'}
          value={input}
          rows={2}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading}
        />
        <div className="ui-cluster">
          <span className="ui-meta">Enter sends. Shift+Enter starts a new line.</span>
          <span className="ui-spacer" />
          <Button type="submit" variant="primary" icon={Send} loading={loading} disabled={!input.trim()}>Send</Button>
        </div>
      </div>
    </form>
  );

  // ── Drawer mode ──────────────────────────────────────────────────────────────
  // The host gives a flex column; the thread scrolls and the composer is pinned. The scroll ref sits on the thread
  // scroller itself, so a new reply scrolls into view (HD-8 A0, HD-7 P4: it sat on an empty end marker and never moved).
  if (mode === 'drawer') {
    return (
      <>
        <div className="ui ui-thread ui-thread--drawer" data-testid="clio-thread" ref={threadRef}>
          {thread}
        </div>
        {composer}
        {assetModal}
      </>
    );
  }

  // ── Page mode (default) ──────────────────────────────────────────────────────
  return (
    <div className="ui ui-chat">
      {showHeader && (
        <div className="ui-inset ui-inset--bar">
          <h1 className="ui-heading">Clio</h1>
          <p className="ui-meta ui-p">{isHelpMode ? 'Ask anything about how Heliconflow works' : 'Ask anything about your pipeline'}</p>
        </div>
      )}
      <div ref={threadRef} className="ui-thread" data-testid="clio-thread">
        <div className="ui-col">{thread}</div>
      </div>
      {composer}
      {assetModal}
    </div>
  );
}
